import { formatPrice, type LegendRow } from '../render/legend.ts';
import { ftToM, pricePerUnitValue, type Units } from '../units/units.ts';

/**
 * Quote PDF data (docs/spec/08-plan-features.md §3): numbering, dates, file name, line items,
 * default terms. Pure; layout and PDF rendering are per platform.
 */

export type QuoteTemplate = 'classic' | 'modern' | 'minimal';

/** Per-project quote data, stored in the project document under `quote` (all optional). */
export interface QuoteData {
  customer?: { name?: string; address?: string; email?: string; phone?: string };
  notes?: string;
  template?: QuoteTemplate;
  depositPercent?: number | null;
}

/** Business branding, stored in the catalog document under `branding` (all optional). */
export interface Branding {
  companyName?: string;
  /** sha256 blob ref of a PNG/JPEG logo (≤ 1 MB). */
  logo?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  licenseNumber?: string;
  /** #RRGGBB accent for the header rule and table header. */
  accentColor?: string;
  terms?: string;
  validityDays?: number;
  depositPercent?: number | null;
  template?: QuoteTemplate;
}

export const DEFAULT_VALIDITY_DAYS = 30;
export const QUOTE_FOOTNOTE_ESTIMATED = 'Lengths are estimated from the photo.';
export const BASIC_QUOTE_FOOTER = 'Made with Better Lights · betterlights.app';

const pad = (n: number) => String(n).padStart(2, '0');

/** Local calendar date as YYYY-MM-DD. */
export function isoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Quote number `Q-<YYYYMMDD>-<last 4 alphanumeric characters of the project id, uppercased>`
 * (local date). Ids with fewer than 4 alphanumerics use all of them.
 */
export function quoteNumber(projectId: string, date: Date): string {
  const tail = projectId.replace(/[^A-Za-z0-9]/g, '').slice(-4).toUpperCase();
  return `Q-${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${tail}`;
}

/** Quote date and valid-until date (date + validityDays, local calendar days) as YYYY-MM-DD. */
export function quoteDates(date: Date, validityDays: number = DEFAULT_VALIDITY_DAYS): { date: string; validUntil: string } {
  const until = new Date(date.getFullYear(), date.getMonth(), date.getDate() + validityDays);
  return { date: isoDate(date), validUntil: isoDate(until) };
}

/**
 * `<project name> – Quote – <YYYY-MM-DD>.pdf` (en dashes U+2013). Characters that are invalid
 * in file names on common platforms (/ \ : * ? " < > |) become "-"; the name is trimmed.
 */
export function quoteFileName(projectName: string, date: Date): string {
  const name = projectName.replace(/[/\\:*?"<>|]/g, '-').trim();
  return `${name} – Quote – ${isoDate(date)}.pdf`;
}

/** Default terms text with the validity filled in. */
export function defaultQuoteTerms(validityDays: number = DEFAULT_VALIDITY_DAYS): string {
  return `This quote is an estimate based on the photo provided. Final measurements and pricing are confirmed on site. Valid for ${validityDays} days.`;
}

/**
 * Money in the quote PDF, always to the cent with en-US grouping: "$1,316.00", "$0.50".
 * The value is first rounded to whole cents (Math.round(value·100), half up).
 * The in-app estimate keeps `formatMoney` (whole units).
 */
export function formatQuoteMoney(value: number, currency: string): string {
  const cents = Math.round(value * 100);
  const abs = Math.abs(cents);
  return `${currency}${cents < 0 ? '-' : ''}${Math.floor(abs / 100).toLocaleString('en-US')}.${String(abs % 100).padStart(2, '0')}`;
}

export interface QuoteLineItem {
  /** Legend row color (#RRGGBB) for the swatch. */
  color: string;
  /** Product name (legend label name). */
  name: string;
  /** Description (legend label product), "" when none. */
  description: string;
  /** Effective length in feet (measured or estimated). */
  feet: number;
  /** True when the length was measured (no "≈"). */
  measured: boolean;
  /** Length cell without unit (the column header carries it): "≈82" / "96" (ft, integer) or "≈25.0" / "29.3" (m, 1 decimal below 100). */
  lengthText: string;
  pricePerFt: number | null;
  /** Price cell without unit: formatPrice(price per ft or per m), e.g. "$28", "$91.86"; null when unpriced. */
  priceText: string | null;
  /** round(feet × pricePerFt, 2) = Math.round(feet × price × 100) / 100, or null when unpriced. */
  subtotal: number | null;
  /** formatQuoteMoney(subtotal), or null. */
  subtotalText: string | null;
}

export interface QuoteLines {
  /** Column headers that carry the unit: "Length (ft)" / "Length (m)", "$/ft" / "$/m". */
  headers: { length: string; price: string };
  items: QuoteLineItem[];
  /** Sum of the rounded line subtotals (summed in integer cents), null when no row is priced. */
  total: number | null;
  totalText: string | null;
  /** round(total × depositPercent / 100, 2) when depositPercent > 0 and the total is known. */
  deposit: number | null;
  depositText: string | null;
  /** "Lengths are estimated from the photo." only when a PRICED row uses the photo estimate and the scale is not calibrated. */
  footnote: string | null;
}

function lengthCell(ft: number, units: Units, measured: boolean): string {
  const prefix = measured ? '' : '≈';
  if (units === 'imperial') return `${prefix}${Math.round(ft)}`;
  const m = ftToM(ft);
  const r = Math.round(m * 10) / 10;
  return `${prefix}${r >= 100 ? String(Math.round(m)) : r.toFixed(1)}`;
}

/**
 * Line items table of the quote PDF: one row per legend row (in legend order), then total and
 * optional deposit, all to the cent so the printed rows always add up. Integer-cent arithmetic:
 * lineCents = Math.round(feet × price × 100); totalCents = Σ lineCents;
 * depositCents = Math.round(totalCents × depositPercent / 100); value = cents / 100.
 * Rows may carry `measured: true` (see measuredLegendRows); `feet` is the effective length.
 * `calibrated` = the project has a manual / calibrated frame width (frameWidthFt != null).
 * Money is unchanged by `units`; only lengths, price cells and headers convert.
 */
export function quoteLineItems(
  rows: (LegendRow & { measured?: boolean })[],
  currency: string,
  opts: { calibrated: boolean; depositPercent?: number | null; units?: Units },
): QuoteLines {
  const units = opts.units ?? 'imperial';
  let totalCents = 0;
  let priced = false;
  let pricedEstimate = false;
  const items = rows.map((r): QuoteLineItem => {
    const price = r.label.pricePerFt;
    const measured = r.measured === true;
    const cents = price != null ? Math.round(r.feet * price * 100) : null;
    if (cents != null) {
      totalCents += cents;
      priced = true;
      if (!measured) pricedEstimate = true;
    }
    const subtotal = cents != null ? cents / 100 : null;
    return {
      color: r.color,
      name: r.label.name,
      description: r.label.product,
      feet: r.feet,
      measured,
      lengthText: lengthCell(r.feet, units, measured),
      pricePerFt: price,
      priceText: price != null ? formatPrice(pricePerUnitValue(price, units), currency) : null,
      subtotal,
      subtotalText: subtotal != null ? formatQuoteMoney(subtotal, currency) : null,
    };
  });
  const total = priced ? totalCents / 100 : null;
  const pct = opts.depositPercent;
  const deposit = priced && pct != null && pct > 0 ? Math.round((totalCents * pct) / 100) / 100 : null;
  const unit = units === 'imperial' ? 'ft' : 'm';
  return {
    headers: { length: `Length (${unit})`, price: `${currency}/${unit}` },
    items,
    total,
    totalText: total != null ? formatQuoteMoney(total, currency) : null,
    deposit,
    depositText: deposit != null ? formatQuoteMoney(deposit, currency) : null,
    footnote: pricedEstimate && !opts.calibrated ? QUOTE_FOOTNOTE_ESTIMATED : null,
  };
}
